import { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Pagination,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';

import { asGermanDateTime } from 'src/common/date';
import { useAppDispatch } from 'src/redux-app-hooks';
import { NotificationSeverity, showSnackbar } from 'src/features/notifications/notifications.slice';
import { OwnerAutocomplete } from 'src/features/crm/components/OwnerAutocomplete';
import { CompanyAutocomplete } from 'src/features/crm/components/CompanyAutocomplete';
import { useHydratedOwner } from 'src/features/crm/useHydratedOwner';
import { Company } from 'src/features/crm/crm.dto';
import { admiFederalStateService, FederalState } from 'src/features/real-estate/federal-state.service';
import {
    InformationAvailabilityStatus,
    ReaCollectionViewCustomerDto,
    ReaCollectionViewReaDto,
    ReaCollectionViewResponseDto,
    ReaCollectionViewSearchDto,
} from './rea-collection-view.dto';
import { admiReaCollectionViewService } from './rea-collection-view.service';

// Backend supports sort on customer-level columns only — the parent is paginated.
const CUSTOMER_SORT_FIELDS = ['customerName', 'region'] as const;
type CustomerSortField = (typeof CUSTOMER_SORT_FIELDS)[number];

const DEFAULT_SORT = 'customerName,asc';
const PAGE_SIZE = 20;

// Bundesländer are fetched at runtime from `/v1/federal-states` rather
// than hard-coded — the backend stores customer_tasks.region as the
// 2-digit code, and the FE resolves it to the localized name from the
// same source the DB seeds use. No more drift between the 16-name
// list here and the catalog.

const INFO_AVAILABILITY_OPTIONS: InformationAvailabilityStatus[] = [
    'MANDATORY_INFO_MISSING',
    'IMPORTANT_INFO_MISSING',
    'ALL_AVAILABLE',
    'NO_SERVICE_CONNECTED',
];

interface SortState {
    field: CustomerSortField;
    direction: 'asc' | 'desc';
}

function parseSort(sortParam: string | null): SortState {
    const [field, direction] = (sortParam || DEFAULT_SORT).split(',');
    const safeField = (CUSTOMER_SORT_FIELDS as readonly string[]).includes(field)
        ? (field as CustomerSortField)
        : 'customerName';
    const safeDirection = direction === 'desc' ? 'desc' : 'asc';
    return { field: safeField, direction: safeDirection };
}

function formatSort(sort: SortState): string {
    return `${sort.field},${sort.direction}`;
}

function parseStatusList(raw: string | null): InformationAvailabilityStatus[] {
    if (!raw) return [];
    return raw
        .split(',')
        .map((s) => s.trim() as InformationAvailabilityStatus)
        .filter((s) => INFO_AVAILABILITY_OPTIONS.includes(s));
}

function ReaCollectionViewPage() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const [response, setResponse] = useState<ReaCollectionViewResponseDto>();
    const [isLoading, setIsLoading] = useState(false);

    // Customer-level filters (above the table)
    const [regionFilter, setRegionFilter] = useState<string>('');
    // Owner state is driven by the URL via `useHydratedOwner`: on first
    // load or any URL id change the hook fetches the owner from HubSpot
    // so the Autocomplete renders a real name. The exposed setter lets
    // the user pick a different owner (or clear via the X) without the
    // hook re-fetching for the URL id it just resolved.
    const [accountManagerOwner, setAccountManagerOwner] = useHydratedOwner(searchParams.get('accountManagerId'));

    // Customer dropdown — deep-linked via the URL `customerId`, which carries
    // the *HubSpot* company id: the backend pins on `customer_tasks.client_id`
    // and the auto-created HubSpot tasks embed the same id in their deep
    // links. There is no company-by-id endpoint, but the list response is
    // *pinned* to this customer when the param is present, so we backfill the
    // human-readable name from `response` once it lands. Picking a company
    // writes its HubSpot `externalId` to the URL (see `changeCustomer`) — not
    // the data-products surrogate `id`, which the backend can't match.
    const customerId = searchParams.get('customerId');
    const [customerFilter, setCustomerFilter] = useState<Company | null>(null);

    // Per-column filters (in the header row of the table)
    const [customerNameFilter, setCustomerNameFilter] = useState('');
    const [reaNameFilter, setReaNameFilter] = useState('');
    const [reaAddressFilter, setReaAddressFilter] = useState('');
    const [servicesMinFilter, setServicesMinFilter] = useState('');
    const [servicesMaxFilter, setServicesMaxFilter] = useState('');
    const [infoStatusFilter, setInfoStatusFilter] = useState<InformationAvailabilityStatus[]>([]);
    const [createdFromFilter, setCreatedFromFilter] = useState<Dayjs | null>(null);
    const [createdToFilter, setCreatedToFilter] = useState<Dayjs | null>(null);
    const [updatedFromFilter, setUpdatedFromFilter] = useState<Dayjs | null>(null);
    const [updatedToFilter, setUpdatedToFilter] = useState<Dayjs | null>(null);
    const [missingInformationFilter, setMissingInformationFilter] = useState('');

    // Bundesländer for the region dropdown. Fetched once at mount; the
    // 16-row response is stable reference data so no refetch on filter
    // change. Empty array while loading — the dropdown shows just the
    // "All regions" option until the response lands.
    const [federalStates, setFederalStates] = useState<FederalState[]>([]);
    useEffect(() => {
        let cancelled = false;
        admiFederalStateService
            .list()
            .then((states) => {
                if (!cancelled) setFederalStates(states);
            })
            .catch((err) => {
                if (cancelled) return;
                console.error('rea-list-view: failed to load federal states', err);
                // Mirror the rea-list-view's other API-failure path: a
                // silent console.error leaves the dropdown empty without
                // any signal to the AM that the regions failed to load.
                dispatch(
                    showSnackbar({
                        message: t('Failed to load regions. Please try again.'),
                        severity: NotificationSeverity.Error,
                    }),
                );
            });
        return () => {
            cancelled = true;
        };
    }, [dispatch, t]);

    const page = Number(searchParams.get('page')) || 1;
    const sort = useMemo(() => parseSort(searchParams.get('sort')), [searchParams]);

    // Hydrate local form state from the URL. URL is the source of truth; this
    // effect keeps the inputs in sync with refresh / back-button navigation.
    useEffect(() => {
        setRegionFilter(searchParams.get('region') ?? '');
        // accountManagerOwner is driven by useHydratedOwner — no manual sync.
        setCustomerNameFilter(searchParams.get('customerName') ?? '');
        setReaNameFilter(searchParams.get('reaName') ?? '');
        setReaAddressFilter(searchParams.get('reaAddress') ?? '');
        setServicesMinFilter(searchParams.get('connectedServicesCountMin') ?? '');
        setServicesMaxFilter(searchParams.get('connectedServicesCountMax') ?? '');
        setInfoStatusFilter(parseStatusList(searchParams.get('informationAvailabilityStatus')));
        const fromDate = (raw: string | null) => (raw ? dayjs(raw) : null);
        setCreatedFromFilter(fromDate(searchParams.get('reaCreatedFrom')));
        setCreatedToFilter(fromDate(searchParams.get('reaCreatedTo')));
        setUpdatedFromFilter(fromDate(searchParams.get('reaUpdatedFrom')));
        setUpdatedToFilter(fromDate(searchParams.get('reaUpdatedTo')));
        setMissingInformationFilter(searchParams.get('missingInformationContains') ?? '');
    }, [searchParams]);

    // Keep the customer dropdown in sync with the URL `customerId`, backfilling
    // the company name from the (pinned) list response once it arrives. The
    // response customer DTO's `id` is the HubSpot company id (mirrored from
    // `customer_tasks.client_id`), i.e. the same id space as the URL param.
    useEffect(() => {
        if (!customerId) {
            setCustomerFilter(null);
            return;
        }
        const name = response?.content.find((c) => c.id === customerId)?.name ?? null;
        setCustomerFilter((prev) => {
            if (prev?.externalId === customerId) {
                // Already showing this customer (e.g. just picked it). Only
                // swap in a better label once the response provides one.
                if (name && name !== prev.companyName) {
                    return { ...prev, companyName: name };
                }
                // Loading finished without resolving a name (stale/unknown id
                // or failed request): swap a still-pending placeholder for the
                // raw id so the dropdown doesn't show "Loading…" forever.
                if (!name && !isLoading && prev.companyName === t('Loading...')) {
                    return { ...prev, companyName: customerId };
                }
                return prev;
            }
            // Fresh deep-link / refresh: seed from the response if present;
            // while the (pinned) list is still loading show a localized
            // placeholder instead of the raw id, falling back to the id only
            // once loading has finished without resolving a name.
            return companyFromId(customerId, name, isLoading ? t('Loading...') : customerId);
        });
    }, [customerId, response, isLoading, t]);

    useEffect(() => {
        const controller = new AbortController();
        const load = async () => {
            setIsLoading(true);
            try {
                const search: ReaCollectionViewSearchDto = {
                    page,
                    size: PAGE_SIZE,
                    sort: formatSort(sort),
                    region: searchParams.get('region'),
                    accountManagerId: searchParams.get('accountManagerId'),
                    customerId: searchParams.get('customerId'),
                    customerName: searchParams.get('customerName'),
                    reaName: searchParams.get('reaName'),
                    reaAddress: searchParams.get('reaAddress'),
                    connectedServicesCountMin: parseIntOrNull(searchParams.get('connectedServicesCountMin')),
                    connectedServicesCountMax: parseIntOrNull(searchParams.get('connectedServicesCountMax')),
                    informationAvailabilityStatus:
                        parseStatusList(searchParams.get('informationAvailabilityStatus')) || null,
                    reaCreatedFrom: dateToInstantStart(searchParams.get('reaCreatedFrom')),
                    reaCreatedTo: dateToInstantEnd(searchParams.get('reaCreatedTo')),
                    reaUpdatedFrom: dateToInstantStart(searchParams.get('reaUpdatedFrom')),
                    reaUpdatedTo: dateToInstantEnd(searchParams.get('reaUpdatedTo')),
                    missingInformationContains: searchParams.get('missingInformationContains'),
                };
                const data = await admiReaCollectionViewService.getReaCollectionView(search);
                if (controller.signal.aborted) return;
                setResponse(data);
            } catch (err) {
                if (controller.signal.aborted) return;
                console.error('rea-collection-view: load failed', err);
                dispatch(
                    showSnackbar({
                        message: t('Failed to load the REA list. Please try again.'),
                        severity: NotificationSeverity.Error,
                    }),
                );
            } finally {
                if (!controller.signal.aborted) setIsLoading(false);
            }
        };
        load();
        return () => controller.abort();
    }, [page, sort, searchParams, dispatch, t]);

    function applyFilters() {
        setSearchParams(
            (prev) => {
                const params = new URLSearchParams(prev);
                params.set('page', '1');
                setOrDelete(params, 'region', regionFilter);
                // Owner.id == HubSpot owner-id == customer_tasks.account_manager_id.
                setOrDelete(params, 'accountManagerId', accountManagerOwner?.id ?? '');
                setOrDelete(params, 'customerName', customerNameFilter.trim());
                setOrDelete(params, 'reaName', reaNameFilter.trim());
                setOrDelete(params, 'reaAddress', reaAddressFilter.trim());
                setOrDelete(params, 'connectedServicesCountMin', servicesMinFilter.trim());
                setOrDelete(params, 'connectedServicesCountMax', servicesMaxFilter.trim());
                setOrDelete(
                    params,
                    'informationAvailabilityStatus',
                    infoStatusFilter.length > 0 ? infoStatusFilter.join(',') : '',
                );
                setOrDelete(params, 'reaCreatedFrom', createdFromFilter?.format('YYYY-MM-DD') ?? '');
                setOrDelete(params, 'reaCreatedTo', createdToFilter?.format('YYYY-MM-DD') ?? '');
                setOrDelete(params, 'reaUpdatedFrom', updatedFromFilter?.format('YYYY-MM-DD') ?? '');
                setOrDelete(params, 'reaUpdatedTo', updatedToFilter?.format('YYYY-MM-DD') ?? '');
                setOrDelete(params, 'missingInformationContains', missingInformationFilter.trim());
                return params;
            },
            { replace: true },
        );
    }

    // The customer filter applies immediately (no Search click needed): we
    // write `customerId` to the URL, which both reloads the list and lets the
    // page be shared/deep-linked. Other filters in the URL are preserved.
    function changeCustomer(company: Company | null) {
        setCustomerFilter(company);
        setSearchParams((prev) => {
            const params = new URLSearchParams(prev);
            // `externalId` (HubSpot id), NOT `id`: the backend matches the
            // param against `customer_tasks.client_id`, which is HubSpot's
            // company id — the surrogate `id` never matches and pins nothing.
            setOrDelete(params, 'customerId', company?.externalId ?? '');
            params.set('page', '1');
            return params;
        });
    }

    function changePage(nextPage: number) {
        setSearchParams((prev) => {
            const params = new URLSearchParams(prev);
            params.set('page', String(nextPage));
            return params;
        });
    }

    function changeSort(field: CustomerSortField) {
        const nextDirection: 'asc' | 'desc' = sort.field === field && sort.direction === 'asc' ? 'desc' : 'asc';
        setSearchParams((prev) => {
            const params = new URLSearchParams(prev);
            params.set('sort', formatSort({ field, direction: nextDirection }));
            params.set('page', '1');
            return params;
        });
    }

    const rows = useMemo(() => flattenCustomersToRows(response?.content ?? []), [response]);

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box>
                <Typography variant="h4" sx={{ mb: 3 }}>
                    {t('REA list view')}
                </Typography>

                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    useFlexGap
                    sx={{ alignItems: 'flex-start', flexWrap: 'wrap', mt: 2 }}
                >
                    <FormControl sx={{ minWidth: 220, flex: 1 }}>
                        <InputLabel id="region-label" shrink>
                            {t('Region')}
                        </InputLabel>
                        <Select
                            labelId="region-label"
                            value={regionFilter}
                            onChange={(e) => setRegionFilter(e.target.value)}
                            input={<OutlinedInput label={t('Region')} size="small" notched />}
                        >
                            <MenuItem value="">{t('All regions')}</MenuItem>
                            {federalStates.map((fs) => (
                                <MenuItem key={fs.code} value={fs.code}>
                                    {fs.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl sx={{ minWidth: 260, flex: 1 }}>
                        <CompanyAutocomplete
                            value={customerFilter}
                            onSelectCompany={changeCustomer}
                            label={t('Customer')}
                            size="small"
                            fullWidth
                        />
                    </FormControl>

                    <FormControl sx={{ minWidth: 260, flex: 1 }}>
                        <OwnerAutocomplete
                            value={accountManagerOwner}
                            onSelectOwner={setAccountManagerOwner}
                            label={t('Select AM')}
                            size="small"
                        />
                    </FormControl>

                    <Box sx={{ minWidth: 140, alignSelf: 'stretch', display: 'flex', alignItems: 'flex-end' }}>
                        <Button variant="contained" onClick={applyFilters} fullWidth>
                            {t('Search')}
                        </Button>
                    </Box>
                </Stack>

                <Box sx={{ mt: 4, position: 'relative' }}>
                    {/*
                     * Don't swap the whole table for a spinner — that drops
                     * the layout and makes pagination/filter clicks feel
                     * laggy. Dim the existing rows and overlay a spinner so
                     * the user sees continuity between page loads.
                     */}
                    {isLoading && (
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'center',
                                pt: 4,
                                zIndex: 1,
                                pointerEvents: 'none',
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    )}
                    <Box
                        sx={{
                            opacity: isLoading ? 0.4 : 1,
                            pointerEvents: isLoading ? 'none' : 'auto',
                            transition: 'opacity 150ms ease',
                        }}
                        aria-busy={isLoading}
                    >
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#fbfcfe' }}>
                                    <TableCell sx={headerCellSx}>
                                        <TableSortLabel
                                            active={sort.field === 'customerName'}
                                            direction={sort.direction}
                                            onClick={() => changeSort('customerName')}
                                        >
                                            {t('Customer name')}
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell sx={headerCellSx}>{t('REA name')}</TableCell>
                                    <TableCell sx={headerCellSx}>{t('REA address')}</TableCell>
                                    <TableCell sx={headerCellSx} align="right">
                                        {t('# of connected services')}
                                    </TableCell>
                                    <TableCell sx={headerCellSx} align="center">
                                        {t('Information availability')}
                                    </TableCell>
                                    <TableCell sx={headerCellSx}>{t('Missing information')}</TableCell>
                                    <TableCell sx={headerCellSx}>{t('REA creation date')}</TableCell>
                                    <TableCell sx={headerCellSx}>{t('REA last modified')}</TableCell>
                                </TableRow>
                                {/*
                                 * Per-column filter row. Press Enter inside any text input
                                 * or click the top Search button to apply.
                                 */}
                                <TableRow sx={{ backgroundColor: '#fbfcfe' }}>
                                    <TableCell sx={filterCellSx}>
                                        <TextField
                                            value={customerNameFilter}
                                            onChange={(e) => setCustomerNameFilter(e.target.value)}
                                            onKeyDown={enterApplies(applyFilters)}
                                            placeholder={t('Search…')}
                                            size="small"
                                            fullWidth
                                        />
                                    </TableCell>
                                    <TableCell sx={filterCellSx}>
                                        <TextField
                                            value={reaNameFilter}
                                            onChange={(e) => setReaNameFilter(e.target.value)}
                                            onKeyDown={enterApplies(applyFilters)}
                                            placeholder={t('Search…')}
                                            size="small"
                                            fullWidth
                                        />
                                    </TableCell>
                                    <TableCell sx={filterCellSx}>
                                        <TextField
                                            value={reaAddressFilter}
                                            onChange={(e) => setReaAddressFilter(e.target.value)}
                                            onKeyDown={enterApplies(applyFilters)}
                                            placeholder={t('Search…')}
                                            size="small"
                                            fullWidth
                                        />
                                    </TableCell>
                                    <TableCell sx={filterCellSx} align="right">
                                        <Stack direction="row" spacing={0.5}>
                                            <TextField
                                                value={servicesMinFilter}
                                                onChange={(e) => setServicesMinFilter(e.target.value)}
                                                onKeyDown={enterApplies(applyFilters)}
                                                placeholder={t('min')}
                                                size="small"
                                                type="number"
                                                slotProps={{ htmlInput: { min: 0, style: { width: 50 } } }}
                                            />
                                            <TextField
                                                value={servicesMaxFilter}
                                                onChange={(e) => setServicesMaxFilter(e.target.value)}
                                                onKeyDown={enterApplies(applyFilters)}
                                                placeholder={t('max')}
                                                size="small"
                                                type="number"
                                                slotProps={{ htmlInput: { min: 0, style: { width: 50 } } }}
                                            />
                                        </Stack>
                                    </TableCell>
                                    <TableCell sx={filterCellSx} align="center">
                                        <Select
                                            multiple
                                            value={infoStatusFilter}
                                            onChange={(e) =>
                                                setInfoStatusFilter(e.target.value as InformationAvailabilityStatus[])
                                            }
                                            size="small"
                                            displayEmpty
                                            renderValue={(selected) =>
                                                selected.length === 0 ? (
                                                    <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                                                        {t('Any')}
                                                    </Typography>
                                                ) : (
                                                    `${selected.length}`
                                                )
                                            }
                                            sx={{ minWidth: 80 }}
                                        >
                                            {INFO_AVAILABILITY_OPTIONS.map((status) => (
                                                <MenuItem key={status} value={status}>
                                                    <Checkbox checked={infoStatusFilter.indexOf(status) > -1} />
                                                    <ListItemText
                                                        primary={t(`information-availability-status.${status}`)}
                                                    />
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </TableCell>
                                    <TableCell sx={filterCellSx}>
                                        {/*
                                         * Backend casts the JSONB array to text and runs a
                                         * case-insensitive LIKE — group names ("Energy",
                                         * "Structural documents") and the special
                                         * `connected_service_missing` token both match.
                                         */}
                                        <TextField
                                            value={missingInformationFilter}
                                            onChange={(e) => setMissingInformationFilter(e.target.value)}
                                            onKeyDown={enterApplies(applyFilters)}
                                            placeholder={t('Contains…')}
                                            size="small"
                                            fullWidth
                                        />
                                    </TableCell>
                                    <TableCell sx={filterCellSx}>
                                        <Stack spacing={0.5}>
                                            <DatePicker
                                                value={createdFromFilter}
                                                onChange={setCreatedFromFilter}
                                                label={t('from')}
                                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                            />
                                            <DatePicker
                                                value={createdToFilter}
                                                onChange={setCreatedToFilter}
                                                label={t('to')}
                                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                            />
                                        </Stack>
                                    </TableCell>
                                    <TableCell sx={filterCellSx}>
                                        <Stack spacing={0.5}>
                                            <DatePicker
                                                value={updatedFromFilter}
                                                onChange={setUpdatedFromFilter}
                                                label={t('from')}
                                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                            />
                                            <DatePicker
                                                value={updatedToFilter}
                                                onChange={setUpdatedToFilter}
                                                label={t('to')}
                                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                            />
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((row) => (
                                    <TableRow key={`${row.customer.id}:${row.rea.id}`}>
                                        {/*
                                         * Repeat the customer name on every row — even when
                                         * consecutive REAs belong to the same customer — so the
                                         * column is never blank and rows stay self-describing
                                         * when sorted/scanned.
                                         */}
                                        <TableCell>{row.customer.name ?? '—'}</TableCell>
                                        <TableCell>
                                            <Link
                                                to={`/real-estate-asset-management?reaId=${row.rea.id}`}
                                                style={{ color: 'inherit', textDecoration: 'underline' }}
                                            >
                                                {row.rea.name}
                                            </Link>
                                        </TableCell>
                                        <TableCell>{row.rea.address ?? '—'}</TableCell>
                                        <TableCell align="right">{row.rea.connectedServicesCount}</TableCell>
                                        <TableCell align="center">
                                            <StatusIndicator status={row.rea.informationAvailabilityStatus} />
                                        </TableCell>
                                        <TableCell>{formatMissing(row.rea.missingInformation, t)}</TableCell>
                                        <TableCell>{asGermanDateTime(row.rea.createdAt)}</TableCell>
                                        <TableCell>{asGermanDateTime(row.rea.updatedAt)}</TableCell>
                                    </TableRow>
                                ))}
                                {!rows.length && !isLoading && (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                                            {t('No matching customers')}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </Box>
                </Box>

                {!!response && response.totalPages > 1 && (
                    <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                        <Pagination
                            variant="outlined"
                            count={response.totalPages}
                            page={page}
                            onChange={(_, next) => changePage(next)}
                        />
                    </Box>
                )}
            </Box>
        </LocalizationProvider>
    );
}

interface Row {
    customer: ReaCollectionViewCustomerDto;
    rea: ReaCollectionViewReaDto;
}

function flattenCustomersToRows(customers: ReaCollectionViewCustomerDto[]): Row[] {
    return customers.flatMap((customer) => customer.reas.map((rea) => ({ customer, rea })));
}

function StatusIndicator({ status }: { status: InformationAvailabilityStatus }) {
    const { t } = useTranslation();
    if (status === 'NO_SERVICE_CONNECTED') {
        return (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t('n/a')}
            </Typography>
        );
    }
    const icon =
        status === 'ALL_AVAILABLE' ? (
            <CheckCircleIcon sx={{ color: 'success.main' }} />
        ) : status === 'IMPORTANT_INFO_MISSING' ? (
            <WarningIcon sx={{ color: 'warning.main' }} />
        ) : (
            <BlockIcon sx={{ color: 'error.main' }} />
        );
    const label = t(`information-availability-status.${status}`);
    return (
        <Tooltip title={label}>
            {/*
             * `tabIndex={0}` + `role="img"` lets keyboard users tab onto the
             * icon to surface the tooltip; without it MUI's Tooltip never
             * opens for non-mouse users.
             */}
            <span tabIndex={0} role="img" aria-label={label} style={{ display: 'inline-flex' }}>
                {icon}
            </span>
        </Tooltip>
    );
}

function formatMissing(codes: string[], t: TFunction): React.ReactNode {
    if (!codes.length) return '—';
    // Translate each entry through the `missing-information.*` namespace so
    // backend-stored group names ("Energy") and the special
    // `connected_service_missing` sentinel both render as proper UI strings.
    // i18next falls back to the raw value via `defaultValue` when no key
    // matches, so we never surface a missing translation as an empty chip.
    return (
        <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
            {codes.map((code) => {
                const isConnectedServiceMissing = code === 'connected_service_missing';
                const label = isConnectedServiceMissing
                    ? t('Connected service missing')
                    : t(`missing-information.${code}`, { defaultValue: code });
                return (
                    <Chip
                        key={code}
                        size="small"
                        label={label}
                        color={isConnectedServiceMissing ? 'warning' : 'default'}
                        variant="outlined"
                    />
                );
            })}
        </Stack>
    );
}

// Minimal `Company` for the customer dropdown's selected value, seeded from a
// deep-linked URL `customerId` — a HubSpot company id, so it goes into
// `externalId` (what `changeCustomer` writes back to the URL) and doubles as
// the local option identity `id` (the data-products surrogate is unknown
// here; `CompanyAutocomplete` matches options on `externalId` instead). Only
// the ids and `companyName` drive the Autocomplete's display; the remaining
// fields are only used when rendering search-result options (which always
// come fully populated from the backend), so empty strings are safe here.
// `fallback` is the label shown when no name is known yet (e.g. "Loading…");
// it defaults to the id.
function companyFromId(id: string, name: string | null, fallback?: string): Company {
    return {
        id,
        externalId: id,
        companyName: name ?? fallback ?? id,
        domain: '',
        city: '',
        zipCode: '',
        country: '',
        address: '',
    };
}

function setOrDelete(params: URLSearchParams, key: string, value: string) {
    if (value) params.set(key, value);
    else params.delete(key);
}

function enterApplies(applyFilters: () => void) {
    return (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter') applyFilters();
    };
}

function parseIntOrNull(raw: string | null): number | null {
    if (raw == null || raw.trim() === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) ? Math.floor(n) : null;
}

// Strict YYYY-MM-DD shape — anything else from the URL is treated as absent.
// Avoid parsing through dayjs: `dayjs("2026-10-25")` is interpreted in local
// time, then `.toISOString()` shifts to UTC and can roll the calendar day
// backwards (e.g. CEST midnight becomes 22:00 UTC the previous day), which
// silently widens or narrows the filter window by 24h.
const YMD = /^\d{4}-\d{2}-\d{2}$/;

function dateToInstantStart(raw: string | null): string | null {
    // YYYY-MM-DD → start-of-day UTC instant; the backend's `>=` lower bound
    // includes the whole day on the FROM side.
    return raw && YMD.test(raw) ? `${raw}T00:00:00Z` : null;
}

function dateToInstantEnd(raw: string | null): string | null {
    // YYYY-MM-DD → end-of-day UTC instant; the backend's `<=` upper bound
    // includes the whole day on the TO side.
    return raw && YMD.test(raw) ? `${raw}T23:59:59.999Z` : null;
}

const headerCellSx = {
    fontWeight: 'bold',
    color: '#1e293b',
    py: 1,
    fontSize: '0.875rem',
};

const filterCellSx = {
    py: 0.5,
    px: 1,
    verticalAlign: 'top',
};

export default ReaCollectionViewPage;
